<template>
  <div>
    <RedesignSearchBarCollapsed
      v-if="collapsable && isCollapsed"
      v-model="model"
      @click="toggle()"
    />

    <div v-else class="flex flex-col md:flex-row gap-4">
      <RedesignSearchDestinationSelect
        v-model="destination"
        class="w-full md:max-w-72"
      />

      <RedesignSearchDateRangePicker
        ref="datePickerRef"
        v-model="period"
        class="w-full md:w-auto"
      />

      <RedesignSearchGuestsSelect
        v-model="guests"
        class="w-full md:w-auto"
        @change="trackGuests"
      />

      <WebccButton variant="cta" class="h-12 grow" @click="performSubmit">
        {{ $t(`${trPrefix}search`) }}
      </WebccButton>

      <WebccButton
        v-if="collapsable"
        variant="white"
        outline
        class="h-12 md:hidden"
        :loading
        @click="toggle"
      >
        {{ $t('www.pages.favorites.close') }}
      </WebccButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DeepReadonly } from 'vue'

const trPrefix = 'www.components.elements.SearchBox.'

const model = defineModel<SearchBarState>({ required: true })

defineProps<{
  collapsable?: boolean
  loading?: boolean
}>()

const emit = defineEmits<{
  submit: [state: DeepReadonly<SearchBarState>]
}>()

const tracking = useTracking()

const datePickerRef = useTemplateRef('datePickerRef')

const guests = propToRef(model, 'guests')
const period = propToRef(model, 'period')
const destination = customRef<SearchApiSuggestion | undefined>(() => ({
  get() {
    return model.value.destination
  },
  set(newValue) {
    model.value = { ...model.value, destination: newValue }

    if (!newValue) return

    tracking.handlers?.qs.destinationSelect(newValue)
    /**
     * DatePicker should only be automatically opened after change within the form.
     * Change from outside should not trigger this opening.
     */
    setTimeout(() => datePickerRef.value?.toggle(true), 0)
  },
}))

const [isCollapsed, toggle] = useToggle()

const isScreenSmall = useScreen().breakpoints.smaller('md')

watchImmediate(isScreenSmall, () => adjustCollapse())

async function performSubmit() {
  tracking.handlers?.qs.search(destination.value, period.value, guests.value)
  emit('submit', readonly(unref(model)))

  adjustCollapse()
}

function trackGuests({ pax, pets }: GuestsValue) {
  tracking.handlers?.qs.paxSelect(pax)
  tracking.handlers?.qs.petsSelect(pets)
}

function adjustCollapse() {
  const hasMinData = !!destination.value || isValidDatePeriod(period.value)
  toggle(isScreenSmall.value && hasMinData)
}
</script>
